import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { AlertCircle, CheckCircle2, Info, Send, Ticket, Users } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  Select,
  Switch,
  Textarea,
  useToast,
} from '@/components/ui'
import { useAuthStore } from '@/stores/auth.ts'
import type { StructureData, Folder, Drive, Share, Responsible } from './types.ts'
import { getInheritedResponsibles } from './tabs/matrix/matrix-helpers.ts'

export interface SaveStructureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  customerId: string
  customerName: string
  data: StructureData
  saving: boolean
  onSubmit: (payload: SavePayload) => Promise<void> | void
}

export interface SavePayload {
  notes?: string
  ticket_contact_person_id?: string | null
  ticket_contact_name?: string | null
  ticket_contact_email?: string | null
  approval_input?: {
    deputyMode: 'only-primary' | 'parallel' | 'primary-then-deputy-after'
    deputyActivateAfterDays?: number
    /** employeeId (als String) → E-Mail-Adresse. */
    contactEmails?: Record<string, string>
  }
  skip_approval?: boolean
}

interface Operation {
  type: string
  id: string
  [key: string]: unknown
}

const DEPUTY_MODE_OPTIONS = [
  {
    value: 'primary-then-deputy-after',
    label: 'Stellvertreter nach 5 Tagen (empfohlen)',
  },
  { value: 'parallel', label: 'Primary + Stellvertreter parallel' },
  { value: 'only-primary', label: 'Nur Primary (Stellvertreter informativ)' },
]

interface CompanyPerson {
  id: number | string
  name: string
  email?: string | null
}

const APPROVAL_RELEVANT = new Set([
  'update_permissions',
  'create_folder',
  'rename_folder',
  'create_group',
  'rename_group',
  'create_share',
  'rename_share',
  'create_drive',
  'create_ou',
  'rename_ou',
  'warn_delete_folder',
  'warn_delete_group',
  'warn_delete_drive',
  'warn_delete_server',
  'warn_delete_ou',
])

function describeOp(op: Operation): string {
  switch (op.type) {
    case 'update_permissions':
      return `Berechtigung „${op['path']}": ${op['old_rights']} → ${op['new_rights']}`
    case 'create_folder':
      return `Neuer Ordner „${op['path']}"`
    case 'rename_folder':
      return `Ordner: „${op['old_path']}" → „${op['new_path']}"`
    case 'create_group':
      return `Neue Gruppe ${op['groupType']}_${op['name']}`
    case 'rename_group':
      return `Gruppe: „${op['old_name']}" → „${op['new_name']}"`
    case 'create_drive':
      return `Neues Laufwerk ${op['letter']}: („${op['label']}")`
    case 'warn_delete_folder':
      return `Löschung: „${op['path']}"`
    case 'warn_delete_group':
    case 'warn_delete_ou':
    case 'warn_delete_server':
      return `Löschung: „${op['name']}"`
    case 'warn_delete_drive':
      return `Löschung: ${op['letter']}: („${op['label']}")`
    default:
      return op.type
  }
}

/** Liste der Verantwortlichen (Rank 0 = Primary) fuer diese Operation. Mit Vererbung fuer Folder. */
function responsiblesForOp(op: Operation, data: StructureData): Responsible[] {
  const folders = new Map(data.folders.map((f) => [f.id, f as Folder]))
  const drives = new Map(data.drives.map((d) => [d.id, d as Drive]))
  const shares = new Map<string, Share>()
  for (const srv of data.servers) for (const sh of srv.shares) shares.set(sh.id, sh)

  const folderOps = new Set(['update_permissions', 'create_folder', 'rename_folder', 'warn_delete_folder'])
  if (folderOps.has(op.type)) {
    const f = folders.get(String(op['id']))
    if (f) {
      if (f.responsibleEmployees && f.responsibleEmployees.length > 0) return f.responsibleEmployees
      const inh = getInheritedResponsibles(f, data.folders, Array.from(shares.values()))
      if (inh) return inh.list
    }
  }

  if (op.type === 'update_permissions' && typeof op['path'] === 'string' && String(op['path']).startsWith('share:')) {
    const sid = String(op['path']).slice('share:'.length)
    const sh = shares.get(sid)
    if (sh?.responsibleEmployees) return sh.responsibleEmployees
  }

  if (op.type === 'create_drive' || op.type === 'update_drive' || op.type === 'warn_delete_drive') {
    const d = drives.get(String(op['id']))
    if (d?.responsibleEmployees && d.responsibleEmployees.length > 0) return d.responsibleEmployees
    if (d?.shareId) {
      const sh = shares.get(d.shareId)
      if (sh?.responsibleEmployees) return sh.responsibleEmployees
    }
  }

  if (op.type === 'create_share' || op.type === 'rename_share') {
    const sh = shares.get(String(op['id']))
    if (sh?.responsibleEmployees) return sh.responsibleEmployees
  }

  // Fallback: erster Share mit Verantwortlichen
  for (const srv of data.servers) {
    for (const sh of srv.shares) {
      if (sh.responsibleEmployees && sh.responsibleEmployees.length > 0) return sh.responsibleEmployees
    }
  }
  for (const d of data.drives) {
    if (d.responsibleEmployees && d.responsibleEmployees.length > 0) return d.responsibleEmployees
  }
  return []
}

interface GroupedApprover {
  employeeId: string
  name: string
  deputyEmployeeId?: string
  deputyName?: string
  ops: Operation[]
}

function groupByApprover(ops: Operation[], data: StructureData): {
  groups: GroupedApprover[]
  uncovered: Operation[]
} {
  const map = new Map<string, GroupedApprover>()
  const uncovered: Operation[] = []
  for (const op of ops) {
    const list = responsiblesForOp(op, data)
    if (list.length === 0) {
      uncovered.push(op)
      continue
    }
    const primary = list[0]!
    const primaryId = String(primary.employeeId)
    if (!map.has(primaryId)) {
      const deputy = list[1]
      map.set(primaryId, {
        employeeId: primaryId,
        name: primary.name,
        deputyEmployeeId: deputy ? String(deputy.employeeId) : undefined,
        deputyName: deputy?.name,
        ops: [],
      })
    }
    map.get(primaryId)!.ops.push(op)
  }
  return { groups: [...map.values()], uncovered }
}

export function SaveStructureDialog({
  open,
  onOpenChange,
  customerId,
  customerName,
  data,
  saving,
  onSubmit,
}: SaveStructureDialogProps) {
  const { toast } = useToast()
  const currentUser = useAuthStore((s) => s.user)
  const isImplec = (currentUser?.email ?? '').toLowerCase().endsWith('@implec.de')

  const [notes, setNotes] = useState('')
  const [deputyMode, setDeputyMode] = useState<'only-primary' | 'parallel' | 'primary-then-deputy-after'>(
    'primary-then-deputy-after',
  )
  const [deputyAfterDays, setDeputyAfterDays] = useState(5)
  const [skipApproval, setSkipApproval] = useState(false)
  const [contactPersonId, setContactPersonId] = useState<string>('')
  const [emailOverrides, setEmailOverrides] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: diffData, isLoading: diffLoading } = useQuery<{ operations: Operation[] }>({
    queryKey: ['it-structure-save-preview', customerId, open],
    queryFn: async () => {
      const r = await apiClient.post(`/it-structures/customers/${customerId}/preview-diff`, { structure_data: data })
      return r.data
    },
    enabled: open,
    retry: false,
  })

  const { data: personsData } = useQuery<CompanyPerson[]>({
    queryKey: ['tanss-company-persons', customerId],
    queryFn: () => apiClient
      .get<{ data: CompanyPerson[] }>(`/tanss/companies/${customerId}/persons`)
      .then((r) => r.data.data),
    enabled: open,
  })

  const ops = diffData?.operations ?? []
  const relevantOps = useMemo(() => ops.filter((o) => APPROVAL_RELEVANT.has(o.type)), [ops])
  const { groups, uncovered } = useMemo(() => groupByApprover(relevantOps, data), [relevantOps, data])

  // Auto-fill E-Mails aus den geladenen Personen, wenn vorhanden.
  const personEmailMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of personsData ?? []) if (p.email) m.set(String(p.id), p.email)
    return m
  }, [personsData])

  useEffect(() => {
    if (open) {
      setNotes('')
      setDeputyMode('primary-then-deputy-after')
      setDeputyAfterDays(5)
      setSkipApproval(false)
      setContactPersonId('')
      setSubmitError(null)
      setEmailOverrides({})
    }
  }, [open])

  // Initial-Fill: fuer jede Gruppe, wenn E-Mail aus TANSS-Person verfuegbar, vorbelegen.
  useEffect(() => {
    if (!open) return
    const next: Record<string, string> = { ...emailOverrides }
    let changed = false
    for (const g of groups) {
      if (!next[g.employeeId] && personEmailMap.has(g.employeeId)) {
        next[g.employeeId] = personEmailMap.get(g.employeeId)!
        changed = true
      }
      if (g.deputyEmployeeId && !next[g.deputyEmployeeId] && personEmailMap.has(g.deputyEmployeeId)) {
        next[g.deputyEmployeeId] = personEmailMap.get(g.deputyEmployeeId)!
        changed = true
      }
    }
    if (changed) setEmailOverrides(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, groups, personEmailMap])

  const hasAssigneeGaps = uncovered.length > 0
  const needsContact = isImplec && relevantOps.length > 0 && !skipApproval

  const canSubmit =
    !saving &&
    !hasAssigneeGaps &&
    (skipApproval ||
      relevantOps.length === 0 ||
      groups.every((g) => (emailOverrides[g.employeeId] ?? '').includes('@'))) &&
    (!needsContact || !!contactPersonId)

  async function submit() {
    setSubmitError(null)
    const selectedContact = personsData?.find((p) => String(p.id) === contactPersonId)
    const payload: SavePayload = {
      notes: notes || undefined,
      ticket_contact_person_id: isImplec ? contactPersonId || null : null,
      ticket_contact_name: isImplec ? selectedContact?.name ?? null : currentUser?.name ?? null,
      ticket_contact_email: isImplec ? selectedContact?.email ?? null : currentUser?.email ?? null,
      approval_input: {
        deputyMode,
        deputyActivateAfterDays: deputyMode === 'primary-then-deputy-after' ? deputyAfterDays : undefined,
        contactEmails: Object.fromEntries(
          Object.entries(emailOverrides).filter(([, v]) => v && v.includes('@')),
        ),
      },
      skip_approval: skipApproval,
    }
    try {
      await onSubmit(payload)
      toast({ title: 'Gespeichert', description: 'Neue Version wurde angelegt.', tone: 'success' })
    } catch (e) {
      setSubmitError((e as Error).message ?? 'Fehler beim Speichern')
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2">
          <Send className="w-5 h-5 text-brand-500" />
          Änderungen speichern — {customerName}
        </span>
      }
      description="Bei relevanten Änderungen werden Freigabe-Mails versendet und ein TANSS-Ticket angelegt."
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={saving} disabled={!canSubmit} onClick={submit}>
            {relevantOps.length > 0 && !skipApproval
              ? `Speichern & ${groups.length} Freigabe${groups.length === 1 ? '' : 'n'} anfragen`
              : 'Speichern'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {diffLoading && <LoadingState label="Änderungen werden ermittelt…" />}

        {!diffLoading && ops.length === 0 && (
          <EmptyState size="sm" icon={<Info />} title="Keine Änderungen" description="Keine Änderungen zur Vorgängerversion." />
        )}

        {ops.length > 0 && (
          <Card>
            <Card.Header>
              <Card.Title className="flex items-center gap-2 text-body">
                <CheckCircle2 className="w-4 h-4 text-brand-500" />
                Änderungen gegenüber letzter Version ({ops.length})
              </Card.Title>
            </Card.Header>
            <ul className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {ops.map((op, i) => (
                <li key={`${op.type}-${op.id}-${i}`} className="px-4 py-2 text-body flex items-start gap-2">
                  <Badge variant={APPROVAL_RELEVANT.has(op.type) ? 'info' : 'neutral'} size="sm">
                    {op.type}
                  </Badge>
                  <span className="text-gray-700 font-mono break-all">{describeOp(op)}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {hasAssigneeGaps && (
          <ErrorState
            size="sm"
            title={`${uncovered.length} Änderung${uncovered.length === 1 ? '' : 'en'} ohne Verantwortlichen`}
            description="Bitte Responsible-Felder auf den betroffenen Ordnern/Drives/Shares setzen, bevor gespeichert werden kann."
            details={uncovered.map(describeOp).join('\n')}
          />
        )}

        {relevantOps.length > 0 && !hasAssigneeGaps && (
          <Card>
            <Card.Header>
              <Card.Title className="flex items-center gap-2 text-body">
                <Users className="w-4 h-4 text-brand-500" />
                Freigaben, die angefragt werden ({groups.length})
              </Card.Title>
            </Card.Header>
            <div className="divide-y divide-gray-100">
              {groups.map((g) => (
                <div key={g.employeeId} className="px-4 py-3 space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-body font-medium text-gray-900">{g.name}</span>
                      <Badge size="sm">
                        {g.ops.length} Änderung{g.ops.length === 1 ? '' : 'en'}
                      </Badge>
                    </div>
                    {g.deputyEmployeeId && (
                      <span className="text-caption text-gray-500">
                        Stellvertreter: {g.deputyName ?? g.deputyEmployeeId}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      type="email"
                      placeholder="E-Mail des Verantwortlichen (Pflicht)"
                      className="px-3 py-2 border border-gray-300 rounded-md text-body focus:outline-none focus:ring-2 focus:ring-brand-500"
                      value={emailOverrides[g.employeeId] ?? ''}
                      onChange={(e) =>
                        setEmailOverrides((prev) => ({ ...prev, [g.employeeId]: e.target.value }))
                      }
                    />
                    {g.deputyEmployeeId && (
                      <input
                        type="email"
                        placeholder="E-Mail Stellvertreter (optional)"
                        className="px-3 py-2 border border-gray-300 rounded-md text-body focus:outline-none focus:ring-2 focus:ring-brand-500"
                        value={emailOverrides[g.deputyEmployeeId] ?? ''}
                        onChange={(e) =>
                          setEmailOverrides((prev) => ({
                            ...prev,
                            [g.deputyEmployeeId!]: e.target.value,
                          }))
                        }
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {relevantOps.length > 0 && (
          <Card>
            <Card.Header>
              <Card.Title className="text-body flex items-center gap-2">
                <Users className="w-4 h-4 text-brand-500" />
                Stellvertreter-Regel
              </Card.Title>
            </Card.Header>
            <Card.Body className="space-y-3">
              <Select
                value={deputyMode}
                onValueChange={(v) => setDeputyMode(v as typeof deputyMode)}
                options={DEPUTY_MODE_OPTIONS}
              />
              {deputyMode === 'primary-then-deputy-after' && (
                <label className="flex items-center gap-2 text-body text-gray-700">
                  Stellvertreter übernimmt nach
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={deputyAfterDays}
                    onChange={(e) => setDeputyAfterDays(Math.max(1, Number(e.target.value) || 1))}
                    className="w-16 px-2 py-1 border border-gray-300 rounded-md text-body"
                  />
                  Tagen ohne Antwort
                </label>
              )}
            </Card.Body>
          </Card>
        )}

        {isImplec && relevantOps.length > 0 && !skipApproval && (
          <Card>
            <Card.Header>
              <Card.Title className="text-body flex items-center gap-2">
                <Ticket className="w-4 h-4 text-brand-500" />
                Ticket-Ansprechpartner (Pflicht für implec-User)
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {personsData && personsData.length > 0 ? (
                <Select
                  value={contactPersonId}
                  onValueChange={(v) => setContactPersonId(v)}
                  options={personsData.map((p) => ({
                    value: String(p.id),
                    label: p.email ? `${p.name} (${p.email})` : p.name,
                  }))}
                  placeholder="Ansprechpartner im TANSS wählen …"
                />
              ) : (
                <div className="text-body text-gray-600 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
                  <span>Keine Ansprechpartner für diese Firma in TANSS gefunden.</span>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {!isImplec && relevantOps.length > 0 && !skipApproval && (
          <Card className="bg-blue-50 border-blue-200">
            <Card.Body className="text-body text-blue-900 flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5" />
              <span>
                Als Kunden-User wirst du selbst als Ticket-Ansprechpartner hinterlegt ({currentUser?.email}).
              </span>
            </Card.Body>
          </Card>
        )}

        {relevantOps.length > 0 && (
          <Card>
            <Card.Body className="flex items-center justify-between">
              <div>
                <p className="text-body font-medium text-gray-900">Freigabe überspringen</p>
                <p className="text-caption text-gray-500">
                  Nur nutzen wenn die Änderungen keine externe Abstimmung erfordern (z. B. interne Tests).
                </p>
              </div>
              <Switch checked={skipApproval} onChange={(e) => setSkipApproval(e.target.checked)} />
            </Card.Body>
          </Card>
        )}

        <Textarea
          label="Notizen (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Begleitkommentar zur Version …"
          rows={3}
        />

        {submitError && <ErrorState size="sm" title="Fehler beim Speichern" description={submitError} />}
      </div>
    </Modal>
  )
}
